/**
 * MeetingViewModal - Модальное окно просмотра встречи
 */
import React from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Avatar,
  Badge,
  Divider,
  Box,
  Button,
} from '@mantine/core';
import {
  IconCalendarEvent,
  IconUser,
  IconUsers,
  IconMessage,
  IconPencil,
  IconBriefcase,
  IconCalendar,
  IconCheck,
  IconClipboardText,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * Форматирование полного имени контакта
 */
const formatContactName = (contact) => {
  if (!contact) return 'Неизвестный контакт';
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  return parts.join(' ') || 'Без имени';
};

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (!user) return 'Не указан';
  if (user.full_name) return user.full_name;
  return user.email;
};

/**
 * Форматирование даты и времени
 */
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Получение инициалов контакта
 */
const getInitials = (contact) => {
  if (!contact) return 'К';
  const first = contact.first_name?.[0]?.toUpperCase() || '';
  const last = contact.last_name?.[0]?.toUpperCase() || '';
  return last + first || first || 'К';
};

/**
 * Компонент для отображения поля
 */
const InfoField = ({ icon: Icon, label, value, children }) => {
  if (!value && !children) return null;

  return (
    <Group gap="sm" align="flex-start">
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: tokens.colors.neutral[2],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={tokens.colors.neutral[7]} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        {children || <Text size="sm">{value}</Text>}
      </Box>
    </Group>
  );
};

const MeetingViewModal = ({
  opened,
  onClose,
  meeting,
  onEdit,
  onAddSummary,
}) => {
  if (!meeting) return null;

  // Список контактов
  const contactNames = meeting.contacts?.map(c => formatContactName(c)).join(', ') || 'Без контактов';
  const firstContact = meeting.contacts?.[0];

  // Список участников
  const participantNames = meeting.participants?.map(p => formatUserName(p)).join(', ') || 'Не указаны';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Встреча"
      size="md"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="lg">
        {/* Заголовок с аватаром контакта */}
        <Group>
          <Avatar size="xl" radius="xl" color="blue">
            {getInitials(firstContact)}
          </Avatar>
          <Box style={{ flex: 1 }}>
            <Group gap="xs" wrap="wrap">
              <Text size="lg" fw={600}>
                {contactNames}
              </Text>
              <Badge size="sm" variant="filled" radius="xl" color="blue" leftSection={<IconCalendarEvent size={10} />}>
                Встреча
              </Badge>
              {meeting.is_completed && (
                <Badge size="sm" variant="filled" radius="xl" color="green" leftSection={<IconCheck size={10} />}>
                  Проведено
                </Badge>
              )}
              {meeting.edit_count > 0 && (
                <Badge size="sm" color="blue" variant="light" radius="xl">
                  Изменено {meeting.edit_count}
                </Badge>
              )}
            </Group>
            {firstContact?.position && (
              <Group gap={4} mt={4}>
                <IconBriefcase size={14} color={tokens.colors.neutral[6]} />
                <Text size="sm" c="dimmed">
                  {firstContact.position}
                </Text>
              </Group>
            )}
          </Box>
        </Group>

        <Divider />

        {/* Информация о встрече */}
        <Stack gap="md">
          {/* С кем встреча */}
          <InfoField icon={IconUsers} label="С кем встреча">
            <Text size="sm" fw={500}>{contactNames}</Text>
          </InfoField>

          {/* Кто проводит */}
          <InfoField icon={IconUser} label="Кто проводит встречу">
            <Text size="sm">{participantNames}</Text>
          </InfoField>

          {/* Дата и время */}
          <InfoField
            icon={IconCalendar}
            label="Дата и время"
            value={formatDateTime(meeting.meeting_datetime)}
          />
        </Stack>

        {/* Комментарий */}
        {meeting.comment && (
          <>
            <Divider />
            <Stack gap="sm">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Комментарий
              </Text>
              <Box
                p="md"
                style={{
                  backgroundColor: tokens.colors.neutral[1],
                  borderRadius: '8px',
                }}
              >
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {meeting.comment}
                </Text>
              </Box>
            </Stack>
          </>
        )}

        {/* Итоги встречи */}
        {meeting.summary && (
          <>
            <Divider />
            <Stack gap="sm">
              <Group gap="xs">
                <IconClipboardText size={14} color={tokens.colors.semantic.success.primary} />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Итоги встречи
                </Text>
              </Group>
              <Box
                p="md"
                style={{
                  backgroundColor: '#E8F5E9',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${tokens.colors.semantic.success.primary}`,
                }}
              >
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {meeting.summary}
                </Text>
              </Box>
            </Stack>
          </>
        )}

        <Divider />

        {/* Кнопки действий */}
        <Group justify="flex-end" gap="sm" wrap="nowrap">
          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            variant="light"
            color="green"
            leftSection={<IconClipboardText size={16} />}
            onClick={() => {
              onClose();
              onAddSummary(meeting);
            }}
          >
            Резюме
          </Button>
          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() => {
              onClose();
              onEdit(meeting);
            }}
          >
            Редактировать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default MeetingViewModal;
